import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";

import { SiteFooter } from "@/components/site-footer";
import { submitContact } from "@/lib/contact.functions";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact · the Vibing Company® — Reply within 24 hours" },
      {
        name: "description",
        content:
          "Tell us who you are, what you need, and we'll reply within 24 hours. A human reads every message.",
      },
      { property: "og:title", content: "Contact · the Vibing Company®" },
      ...ogImageMeta("contact", "Share card: contact the Vibing Company"),
      {
        property: "og:description",
        content: "A simple four-step form. A human replies within 24 hours.",
      },
      { property: "og:url", content: "https://vibing.inc/contact" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/contact" }],
  }),
  component: ContactPage,
});

type ServiceOption = {
  id: string;
  name: string;
  blurb: string;
  group: "build" | "support" | "general";
};

const SERVICES: ServiceOption[] = [
  { id: "Lovable development", name: "Lovable development", blurb: "Build and ship a polished Lovable app.", group: "build" },
  { id: "MVP development", name: "MVP development", blurb: "Turn an idea into a real first release.", group: "build" },
  { id: "UI/UX redesign", name: "UI/UX redesign", blurb: "Make the product clearer, sharper and more distinctive.", group: "build" },
  { id: "Vibe coding rescue", name: "Vibe coding rescue", blurb: "Something broke or stalled. We unstick it.", group: "support" },
  { id: "Lovable Cloud migration", name: "Lovable Cloud migration", blurb: "Move your backend without losing sleep.", group: "support" },
  { id: "App development consulting", name: "App development consulting", blurb: "Get a senior second opinion on the hard decisions.", group: "support" },
  { id: "General inquiry", name: "General inquiry", blurb: "Not sure yet. Let's talk it through.", group: "general" },
];

const STEPS = [
  { n: 1, label: "You" },
  { n: 2, label: "Service" },
  { n: 3, label: "Details" },
  { n: 4, label: "Sent" },
] as const;

function ContactPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Wizard />
      </main>
      <SiteFooter />
    </div>
  );
}


function ProgressBar({ step }: { step: number }) {
  const total = STEPS.length;
  const pct = ((Math.min(step, total) - 1) / (total - 1)) * 100;
  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
          Step {Math.min(step, total)} / {total}
        </p>
        <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
          [ ⏱ ] Reply within 24h
        </p>
      </div>
      <div className="mt-4 relative h-px w-full bg-foreground/10 overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-foreground"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 22 }}
        />
      </div>
      <ol className="mt-4 grid grid-cols-4 gap-2 md:gap-4">
        {STEPS.map((s) => {
          const active = step === s.n;
          const done = step > s.n;
          return (
            <li key={s.n} className="flex items-center gap-2 min-w-0">
              <span
                className={`grid place-items-center h-6 w-6 rounded-full border text-[11px] font-mono shrink-0 transition ${
                  done
                    ? "bg-foreground text-background border-foreground"
                    : active
                    ? "border-foreground text-foreground"
                    : "border-foreground/20 text-muted-foreground"
                }`}
              >
                {done ? "✓" : s.n}
              </span>
              <span
                className={`text-xs md:text-sm truncate transition ${
                  active ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Wizard() {
  const send = useServerFn(submitContact);
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()), [email]);
  const canStep1 = name.trim().length > 0 && emailValid;
  const canStep2 = !!service;
  const canStep3 = message.trim().length >= 10;

  async function submit() {
    if (sending) return;
    setSending(true);
    setErrorMsg(null);
    try {
      const prefix = service ? `Interested in: ${service}\n\n` : "";
      await send({
        data: {
          name: name.trim(),
          email: email.trim(),
          message: `${prefix}${message.trim()}`,
          budget: null,
        },
      });
      setStep(4);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setSending(false);
    }
  }

  return (
    <section
      aria-labelledby="contact-h"
      className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-24 md:pb-32"
    >
      {/* Header */}
      <div className="mb-10 md:mb-14">
        <h1
          id="contact-h"
          className="font-display tracking-[-0.035em] leading-[1.02]"
          style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
        >
          {step < 4 ? (
            <>
              Write us
              <br />
              <span className="italic text-muted-foreground">a few lines.</span>
            </>
          ) : (
            <>
              Got it.
              <br />
              <span className="italic text-muted-foreground">Talk soon.</span>
            </>
          )}
        </h1>
        <p className="mt-6 max-w-[52ch] text-base md:text-lg text-muted-foreground leading-snug">
          {step < 4
            ? "Four small steps. A human reads every message and replies within 24 hours."
            : "Your message landed in our inbox. Expect a reply within 24 hours, often sooner."}
        </p>
      </div>

      {/* Card */}
      <div className="rounded-3xl border border-border bg-background shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.18)] overflow-hidden">
        <div className="px-6 md:px-10 pt-8 md:pt-10">
          <ProgressBar step={step} />
        </div>

        <div className="px-6 md:px-10 py-10 md:py-14 min-h-[420px] relative">
          <AnimatePresence mode="wait" initial={false}>
            {step === 1 && (
              <StepShell key="s1" title="Who's writing?" subtitle="So we know who to reply to.">
                <div className="space-y-8 max-w-[44ch]">
                  <Field label="Your name" htmlFor="name">
                    <input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoFocus
                      maxLength={120}
                      autoComplete="name"
                      placeholder="Ada Lovelace"
                      className="w-full bg-transparent border-0 border-b border-foreground/30 focus:border-foreground outline-none py-3 text-xl md:text-2xl font-display tracking-[-0.02em] placeholder:text-muted-foreground focus:ring-0"
                    />
                  </Field>
                  <Field label="Email" htmlFor="email">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={254}
                      autoComplete="email"
                      placeholder="ada@calculus.engine"
                      className="w-full bg-transparent border-0 border-b border-foreground/30 focus:border-foreground outline-none py-3 text-xl md:text-2xl font-display tracking-[-0.02em] placeholder:text-muted-foreground focus:ring-0"
                    />
                  </Field>
                </div>
              </StepShell>
            )}

            {step === 2 && (
              <StepShell
                key="s2"
                title="What can we help with?"
                subtitle="Pick the one that fits best. You can clarify on the next step."
              >
                <ServiceGroups selected={service} onSelect={setService} />
              </StepShell>
            )}

            {step === 3 && (
              <StepShell
                key="s3"
                title="Tell us more."
                subtitle={
                  service
                    ? `Re: ${service}. A link, what's stuck, what you'd like to ship.`
                    : "A link, what's stuck, what you'd like to ship."
                }
              >
                <label htmlFor="contact-message" className="sr-only">
                  Tell us more about your project
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  aria-describedby="contact-message-hint"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  autoFocus
                  minLength={10}
                  maxLength={5000}
                  rows={8}
                  placeholder="The more context, the better the first reply."
                  className="w-full bg-transparent border-0 border-b border-foreground/30 focus:border-foreground outline-none py-3 text-base md:text-lg leading-relaxed placeholder:text-muted-foreground focus:ring-0 resize-none"
                />
                <p
                  id="contact-message-hint"
                  className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground"
                >
                  {message.trim().length} / 5000 · min 10 characters
                </p>
                <div aria-live="assertive">
                  {errorMsg && (
                    <p className="mt-6 text-sm text-destructive">Error: {errorMsg}</p>
                  )}
                </div>
              </StepShell>
            )}

            {step === 4 && (
              <StepShell key="s4" role="status" title="Message received." subtitle="A human will reply within 24 hours.">
                <div className="space-y-6 max-w-[52ch]">
                  <div className="rounded-2xl border border-border p-5 md:p-6 bg-foreground/[0.02]">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">Summary</p>
                    <dl className="mt-4 divide-y divide-border text-sm">
                      <SummaryRow label="From" value={`${name} · ${email}`} />
                      <SummaryRow label="Service" value={service || "—"} />
                      <SummaryRow label="Reply window" value="Within 24 hours" />
                    </dl>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition"
                    >
                      Back to home <span aria-hidden>→</span>
                    </Link>
                    <a
                      href="mailto:jon@vibing.inc"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-foreground/25 text-sm text-foreground hover:border-foreground transition"
                    >
                      Email jon@vibing.inc
                    </a>
                  </div>
                </div>
              </StepShell>
            )}
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        {step < 4 && (
          <div className="px-6 md:px-10 py-5 md:py-6 border-t border-border flex items-center justify-between gap-4 bg-foreground/[0.015]">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground transition inline-flex items-center gap-2"
            >
              <span aria-hidden>←</span> Back
            </button>
            <div className="flex items-center gap-3">
              <p className="hidden md:block text-xs text-muted-foreground">
                {step === 1 && (canStep1 ? "Looks good." : "Name and a valid email.")}
                {step === 2 && (canStep2 ? "Locked in." : "Pick one to continue.")}
                {step === 3 && (canStep3 ? "Ready to send." : "A few more words.")}
              </p>
              {step < 3 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-foreground text-background text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition"
                >
                  Continue <span aria-hidden>→</span>
                </button>
              )}
              {step === 3 && (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canStep3 || sending}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-foreground text-background text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition"
                >
                  {sending ? "Sending…" : "Send message"}
                  {!sending && <span aria-hidden>→</span>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reassurance footer */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
        <p>
          Or just email{" "}
          <a href="mailto:jon@vibing.inc" className="text-foreground underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground transition">
            jon@vibing.inc
          </a>
        </p>
        <p>
          By sending you agree we may store your message to reply.{" "}
          <Link to="/privacy" className="underline underline-offset-4">Privacy</Link>.
        </p>
      </div>
    </section>
  );
}

function StepShell({
  title,
  subtitle,
  children,
  role,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  role?: string;
}) {
  return (
    <motion.div
      role={role}
      initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -14, filter: "blur(6px)" }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <h2 className="font-display tracking-[-0.03em] text-3xl md:text-4xl">{title}</h2>
      <p className="mt-2 text-muted-foreground max-w-[52ch]">{subtitle}</p>
      <div className="mt-8 md:mt-10">{children}</div>
    </motion.div>
  );
}

function ServiceGroups({
  selected,
  onSelect,
}: {
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const build = SERVICES.filter((s) => s.group === "build");
  const support = SERVICES.filter((s) => s.group === "support");
  const general = SERVICES.filter((s) => s.group === "general");
  return (
    <div className="space-y-8">
      <Group label="Build something">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {build.map((s) => (
            <ServiceCard key={s.id} opt={s} active={selected === s.id} onSelect={() => onSelect(s.id)} />
          ))}
        </div>
      </Group>
      <Group label="Fix, move or advise">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {support.map((s) => (
            <ServiceCard key={s.id} opt={s} active={selected === s.id} onSelect={() => onSelect(s.id)} />
          ))}
        </div>
      </Group>
      <Group label="Just say hi">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {general.map((s) => (
            <ServiceCard key={s.id} opt={s} active={selected === s.id} onSelect={() => onSelect(s.id)} />
          ))}
        </div>
      </Group>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">{label}</p>
      {children}
    </div>
  );
}

function ServiceCard({ opt, active, onSelect }: { opt: ServiceOption; active: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`group relative text-left p-4 md:p-5 rounded-2xl border transition min-h-[6.5rem] flex flex-col justify-between ${
        active
          ? "border-foreground bg-foreground text-background shadow-[0_10px_30px_-15px_rgba(0,0,0,0.35)]"
          : "border-foreground/15 bg-background hover:border-foreground/40 hover:-translate-y-0.5"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-display text-lg md:text-xl tracking-[-0.02em] leading-tight">{opt.name}</span>
        <span
          aria-hidden
          className={`mt-1 grid place-items-center h-5 w-5 rounded-full border text-[10px] leading-none ${
            active
              ? "border-background bg-background text-foreground"
              : "border-foreground/30 text-transparent group-hover:border-foreground/60"
          }`}
        >
          ✓
        </span>
      </div>
      <div className="mt-3 space-y-1.5">
        <p className={`text-xs md:text-sm leading-snug ${active ? "text-background/80" : "text-muted-foreground"}`}>
          {opt.blurb}
        </p>
      </div>
    </button>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right text-foreground max-w-[28ch] break-words">{value}</dd>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block font-mono text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
